import { Fragment, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Store, StoreGroup, StoreSource } from '../api/types';
import { useAuth } from '../store/auth';
import { useEditor } from '../store/editor';
import { useStoreSlice } from '../store/storeRegistry';
import StoreEditDialog from './StoreEditDialog';

// Module-level (not per-render) so the zustand selector below returns the SAME
// reference on every call when there's no signed-in org — a fresh `?? []`
// literal inside a useAuth selector reads as a changed snapshot on every
// render and spins React into "Maximum update depth exceeded".
const EMPTY_GROUPS: StoreGroup[] = [];

export default function StoreSelector({ compact = false }: { compact?: boolean }) {
  const { selectedStoreId, setSelectedStoreId } = useStoreSlice();
  // Store creation is admin-only server-side (auth H1) — hide the entry point.
  const isAdmin = useAuth((s) => s.me?.role === 'admin');
  // Access levels (2026-09-02): group names for the popover's heading dividers.
  const groups = useAuth((s) => s.me?.groups ?? EMPTY_GROUPS);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const storesQ = useQuery({
    queryKey: ['stores'],
    queryFn: api.listStores,
    // Pitfall #10: refetchInterval is CONDITIONAL. Only poll when at least one
    // store has deletion_status set; otherwise no polling overhead.
    refetchInterval: (q) =>
      q.state.data?.some((s) => s.deletion_status) ? 5000 : false,
  });

  function blankStore(): Store {
    const now = new Date().toISOString();
    const src: StoreSource = { type: 'feed', feed_url: null };
    return {
      id: '', name: '', source: src,
      default_assets: {}, default_disclaimer: null,
      sample_product_ids: [],
      created_at: now, updated_at: now,
    };
  }

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const stores = storesQ.data ?? [];
  const current = stores.find((s) => s.id === selectedStoreId);
  const display = current?.name ?? selectedStoreId;

  // Group headings (access levels, 2026-09-02): only worth the visual noise
  // once the visible stores actually span more than one group — a single-org,
  // single-group client sees the same flat list as before.
  const groupsById = new Map(groups.map((g) => [g.id, g.name]));
  const distinctGroups = new Set(stores.map((s) => s.group_id ?? null));
  const showHeadings = distinctGroups.size > 1;
  const ordered = [...stores].sort((a, b) => (a.group_id ?? '~').localeCompare(b.group_id ?? '~'));

  // Auto-correct: when a store gets deleted (cascade strips it from
  // stores.json) but Zustand still has its id persisted in localStorage,
  // switch to the first remaining store so the trigger doesn't linger
  // showing the dead id.
  useEffect(() => {
    if (storesQ.isSuccess && stores.length > 0 && !current) {
      setSelectedStoreId(stores[0].id);
    }
  }, [storesQ.isSuccess, stores, current, setSelectedStoreId]);

  const editorDirty = useEditor((s) => s.dirty);

  function pick(id: string) {
    if (id !== selectedStoreId) {
      // W-C [UX-11]: switching stores refetches the config and clobbers the
      // editor's working copy — confirm before discarding unsaved edits.
      if (
        editorDirty &&
        !window.confirm('You have unsaved edits. Switch store and discard them?')
      ) {
        setOpen(false);
        return;
      }
      setSelectedStoreId(id);
      qc.invalidateQueries({ queryKey: ['configs'] });
      qc.invalidateQueries({ queryKey: ['samples'] });
      qc.invalidateQueries({ queryKey: ['config'] });
    }
    setOpen(false);
  }

  let lastGroup: string | null | undefined;

  return (
    <div className="relative flex items-center gap-2" data-testid="store-selector">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center rounded-full border bg-white/85 backdrop-blur py-1.5 text-xs font-semibold transition-colors ${compact ? 'gap-1.5 px-2.5' : 'gap-2.5 px-3.5'} ${
          open
            ? 'border-ff-forest/40 text-ff-forest shadow-soft'
            : 'border-ff-sand/80 text-ff-mute hover:text-ff-forest hover:border-ff-forest/30'
        }`}
      >
        {!compact && <span className="text-[10px] uppercase tracking-widish text-ff-mute/70">Store</span>}
        <span className={`truncate text-[13px] font-medium normal-case tracking-normal text-ff-ink/85 ${compact ? 'max-w-24' : 'max-w-36'}`}>
          {display}
        </span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 12 12"
          aria-hidden="true"
          className={`transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`}
        >
          <path
            d="M2 4 L6 8 L10 4"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {current && !compact && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={`Edit store ${current.name}`}
          title="Edit store"
          className="grid place-items-center h-7 w-7 rounded-full border border-ff-sand/80 bg-white/70 text-ff-mute hover:text-ff-forest hover:border-ff-forest/30 transition-colors"
          data-testid="store-edit-cog"
        >
          <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
            <path
              d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Zm5.46 3.36.96.84a.6.6 0 0 1 .14.74l-.93 1.6a.6.6 0 0 1-.7.27l-1.21-.4a5.4 5.4 0 0 1-1.18.68l-.25 1.27a.6.6 0 0 1-.59.49h-1.86a.6.6 0 0 1-.59-.49l-.25-1.27a5.4 5.4 0 0 1-1.18-.68l-1.21.4a.6.6 0 0 1-.7-.27l-.93-1.6a.6.6 0 0 1 .14-.74l.96-.84a5.4 5.4 0 0 1 0-1.36l-.96-.84a.6.6 0 0 1-.14-.74l.93-1.6a.6.6 0 0 1 .7-.27l1.21.4c.36-.27.76-.5 1.18-.68l.25-1.27A.6.6 0 0 1 7.06 2h1.86a.6.6 0 0 1 .59.49l.25 1.27c.42.18.82.41 1.18.68l1.21-.4a.6.6 0 0 1 .7.27l.93 1.6a.6.6 0 0 1-.14.74l-.96.84c.05.45.05.91 0 1.36Z"
              stroke="currentColor"
              strokeWidth="1.1"
              fill="none"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      {open && (
        <div
          ref={popRef}
          className="absolute top-full z-50 right-0 mt-2 min-w-[240px] rounded-xl border border-ff-sand/70 bg-white shadow-hover overflow-hidden origin-top-right animate-rise"
        >
          <div className="px-3 py-2 border-b border-ff-sand/60 font-mono text-[10px] uppercase tracking-widish text-ff-mute">
            Stores
          </div>
          <ul className="py-1 max-h-72 overflow-auto">
            {stores.length === 0 && (
              <li className="px-3 py-2 text-xs text-ff-mute italic">no stores</li>
            )}
            {ordered.map((s) => {
              const showHeading = showHeadings && s.group_id !== lastGroup;
              lastGroup = s.group_id ?? null;
              return (
                <Fragment key={s.id}>
                  {showHeading && (
                    <li
                      data-testid={`store-group-heading-${s.group_id ?? 'ungrouped'}`}
                      className="px-3 pt-2 pb-1 font-mono text-[10px] uppercase tracking-widish text-ff-mute"
                    >
                      {s.group_id ? groupsById.get(s.group_id) ?? s.group_id : 'Ungrouped'}
                    </li>
                  )}
                  <li
                    className={`relative flex items-center hover:bg-ff-mint/40 transition-colors ${
                      s.id === selectedStoreId ? 'bg-ff-mint/30' : ''
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => pick(s.id)}
                      data-testid={`store-option-${s.id}`}
                      className="flex-1 text-left px-3 py-2 flex items-center justify-between gap-3"
                    >
                      <span className="text-sm text-ff-ink/90">{s.name}</span>
                      <span className="font-mono text-[10px] uppercase tracking-widish text-ff-mute">
                        {s.id}
                      </span>
                    </button>
                    {/* Pitfall #13: literal strings — yellow "deleting…" (U+2026), red "delete failed — retry?" (U+2014).
                        Rendered as siblings of the row button (not nested) to keep the HTML valid. */}
                    {s.deletion_status === 'in_progress' && (
                      <span
                        className="absolute right-2 rounded-full bg-ff-warning-soft px-2 py-0.5 text-[10px] text-ff-warning pointer-events-none"
                        data-testid={`store-deleting-pill-${s.id}`}
                      >
                        deleting…
                      </span>
                    )}
                    {s.deletion_status === 'failed' && (
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm(`Retry deleting ${s.id}? (Cascade re-runs from phase 1, idempotent.)`)) {
                            api.deleteStore(s.id)
                              .then(() => qc.invalidateQueries({ queryKey: ['stores'] }))
                              .catch((err) => alert(`Retry failed: ${err?.message ?? err}`));
                          }
                        }}
                        className="absolute right-2 rounded-full bg-ff-danger-soft px-2 py-0.5 text-[10px] text-ff-danger hover:bg-ff-danger-soft/70"
                        data-testid={`store-failed-retry-${s.id}`}
                      >
                        delete failed — retry?
                      </button>
                    )}
                  </li>
                </Fragment>
              );
            })}
          </ul>
          {isAdmin && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setCreating(true);
              }}
              className="w-full text-left px-3 py-2 border-t border-dashed border-ff-sand/60 text-ff-forest font-medium hover:bg-ff-mint/40"
              data-testid="store-selector-add-new"
            >
              + New store…
            </button>
          )}
        </div>
      )}
      {editing && current && (
        <StoreEditDialog store={current} onClose={() => setEditing(false)} />
      )}
      {creating && (
        <StoreEditDialog
          store={blankStore()}
          mode="create"
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}
